/**
 * CreativePanel — the creative stage of the campaign canvas.
 *
 * Concept list with include toggles, rendition chips, platform previews, and
 * the full image-generation lifecycle from the redesign brief:
 * - async per-concept generation: shimmer skeleton + "generating" pill, cards
 *   fill in independently (a poll reconciles jobs while any card generates)
 * - 3-take variation strip; the picked take is the master for all renditions
 *   and previews (a canvas_edit, so it replays)
 * - per-concept regeneration via an inline row — free-text direction + quick
 *   chips; only that card returns to generating; previous takes stay one
 *   Undo away (full version stack kept server-side)
 * - failure state with retry; other concepts unaffected
 * - upload dropzone: Basic tier's generation-free path into the same set
 */

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/services/api';
import { ConceptArt } from './conceptArt';
import { CreativePreviews } from './CreativePreviews';
import { MediaLibrary } from '@/components/media/MediaLibrary';
import type { CampaignState, CreativeConcept, Gate } from './types';
import { getStrategyPlatforms } from './types';

const formatLabel = (format?: string, platform?: string) => {
  if (format === 'feed_1x1') return 'Meta 1:1';
  if (format === 'story_9x16') return 'Meta 9:16';
  if (format === 'standard_pin') return 'Pinterest 2:3';
  if (format === 'promoted_1_91x1') return 'X 1.91:1';
  if (format === 'display') return 'Google display';
  return `${platform ?? ''} ${format ?? ''}`.trim() || 'asset';
};

const QUICK_CHIPS: Array<[string, string]> = [
  ['Brighter', 'Brighter, more contrast'],
  ['More product', 'Show the product larger'],
  ['New direction', 'Completely new direction'],
];

function defaultRenditions(conceptId: string, platforms: string[]) {
  const renditions = [];
  if (platforms.length === 0) platforms = ['meta'];
  for (const platform of platforms) {
    if (platform === 'meta') {
      renditions.push(
        { id: `${conceptId}-meta-1x1`, platform: 'meta', format: 'feed_1x1', enabled: true },
        { id: `${conceptId}-meta-9x16`, platform: 'meta', format: 'story_9x16', enabled: true },
      );
    } else if (platform === 'pinterest') {
      renditions.push({
        id: `${conceptId}-pin-std`, platform: 'pinterest', format: 'standard_pin', enabled: true,
      });
    } else if (platform === 'twitter' || platform === 'x') {
      // X's in-feed link ad is 1.91:1 (the website card spec), not the
      // generic display rendition every other platform falls back to.
      renditions.push({
        id: `${conceptId}-x-promoted`, platform: 'twitter', format: 'promoted_1_91x1', enabled: true,
      });
    } else {
      renditions.push({
        id: `${conceptId}-${platform}-disp`, platform, format: 'display', enabled: true,
      });
    }
  }
  return renditions;
}

export interface CreativePanelProps {
  state: CampaignState;
  readonly: boolean;
  onEdit: (path: string, value: unknown, label?: string) => Promise<unknown>;
  onApprove: (gate: Gate) => void;
  approving: boolean;
  isFlashing: (path: string) => boolean;
  conversationId?: string;
  /** Re-fetch the server projection (used by the generation poll) */
  onRefresh?: () => void;
  /** Uploaded conversation images — used as preview fallback when a concept has no generated variations */
  uploadedImages?: Array<{ url: string }>;
}

export const CreativePanel: React.FC<CreativePanelProps> = ({
  state,
  readonly,
  onEdit,
  onApprove,
  approving,
  isFlashing,
  conversationId,
  onRefresh,
  uploadedImages,
}) => {
  const concepts = state.creativeSet ?? [];
  const included = concepts.filter((c) => c.included !== false);
  const anyGenerating = concepts.some((c) => c.status === 'generating');

  const strategyPlatforms = getStrategyPlatforms(state);
  const inStrategy = (p: string) =>
    strategyPlatforms.size === 0 || strategyPlatforms.has(p);

  // View state (not campaign state): preview expansion + open regen rows.
  const [openPreviews, setOpenPreviews] = useState<Set<string>>(new Set());
  const [regenOpen, setRegenOpen] = useState<Set<string>>(new Set());
  const [regenText, setRegenText] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [uploading, setUploading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  // Which idea the detail card shows (view state only).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // While any concept is generating, poll the reconcile endpoint — it
  // persists completion patches server-side; we just re-fetch the projection.
  useEffect(() => {
    if (!anyGenerating || !conversationId) return;
    const interval = setInterval(async () => {
      try {
        await apiClient.get(`/api/v1/campaigns/${conversationId}/concepts/generation-status`);
      } catch {
        // transient — keep polling
      }
      onRefresh?.();
    }, 3500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyGenerating, conversationId]);

  const regenerate = async (concept: CreativeConcept, direction?: string) => {
    if (!conversationId || busy[concept.id]) return;
    setBusy((prev) => ({ ...prev, [concept.id]: true }));
    try {
      await apiClient.post(
        `/api/v1/campaigns/${conversationId}/concepts/${concept.id}/regenerate`,
        { direction: direction || null },
      );
      setRegenOpen((prev) => {
        const next = new Set(prev);
        next.delete(concept.id);
        return next;
      });
      setRegenText((prev) => ({ ...prev, [concept.id]: '' }));
      onRefresh?.();
    } catch {
      // Error surfaces on next poll/refresh; keep the row open for retry.
    } finally {
      setBusy((prev) => ({ ...prev, [concept.id]: false }));
    }
  };

  const handleAdjustmentSave = async (
    conceptId: string,
    format: string,
    crop: { x_frac: number; y_frac: number; w_frac: number; h_frac: number },
    targetW: number,
    targetH: number,
  ) => {
    const updated = concepts.map((c) =>
      c.id === conceptId
        ? {
            ...c,
            adjustments: {
              ...c.adjustments,
              [format]: { crop, textLayers: c.adjustments?.[format]?.textLayers ?? [], targetWidth: targetW, targetHeight: targetH },
            },
          }
        : c,
    );
    await onEdit('creativeSet', updated, `Adjusted image position for ${format}`);
  };

  const undoRegen = async (concept: CreativeConcept) => {
    if (!conversationId || busy[concept.id]) return;
    setBusy((prev) => ({ ...prev, [concept.id]: true }));
    try {
      await apiClient.post(
        `/api/v1/campaigns/${conversationId}/concepts/${concept.id}/undo-regeneration`,
      );
      onRefresh?.();
    } catch {
      // Non-fatal
    } finally {
      setBusy((prev) => ({ ...prev, [concept.id]: false }));
    }
  };

  // Attach a finished creative from the media library / Ad Studio to the set. Mirrors
  // handleUpload: a library asset becomes an "uploaded"-source concept (status ready,
  // its image as the single take). Finished ads already carry baked-in copy, so they
  // publish as a plain image — no dynamic-creative text assembly.
  const handleLibrarySelect = async (asset: {
    id: string;
    public_url: string;
    media_type: 'image' | 'video';
    original_filename?: string;
    filename?: string;
  }) => {
    if (!asset?.public_url) return;
    const platforms = Object.keys(state.budget?.split ?? {});
    const conceptId = `lib-${asset.id ?? Date.now()}`;
    const addition: CreativeConcept = {
      id: conceptId,
      concept: asset.original_filename?.replace(/\.[^.]+$/, '') || 'Ad Studio creative',
      included: true,
      source: 'uploaded',
      status: 'ready',
      mediaType: asset.media_type === 'video' ? 'video' : 'image',
      // Library/Ad Studio creatives are finished ads (copy baked in) by default →
      // publish plain, no DCO. The per-concept toggle can flip this if it's a plain image.
      bakedCopy: true,
      copyVariants: 2,
      variations: [{ id: 'v1', assetUrl: asset.public_url, assetId: asset.id }],
      selectedVariation: 'v1',
      renditions: defaultRenditions(conceptId, platforms),
    };
    setShowLibrary(false);
    await onEdit(
      'creativeSet',
      [...concepts, addition],
      `Added a creative from your library to the set`,
    );
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || uploading) return;
    setUploading(true);
    try {
      const platforms = Object.keys(state.budget?.split ?? {});
      const additions: CreativeConcept[] = [];
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue;
        const formData = new FormData();
        formData.append('file', file);
        const response = await apiClient.post('/api/v1/media/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const asset = response.data?.asset;
        if (!asset?.public_url) continue;
        const conceptId = `u-${asset.id ?? Date.now()}`;
        additions.push({
          id: conceptId,
          concept: (file.name.replace(/\.[^.]+$/, '') || 'Uploaded creative'),
          included: true,
          source: 'uploaded',
          status: 'ready',
          copyVariants: 2,
          variations: [{ id: 'v1', assetUrl: asset.public_url, assetId: asset.id }],
          selectedVariation: 'v1',
          renditions: defaultRenditions(conceptId, platforms),
        });
      }
      if (additions.length > 0) {
        await onEdit(
          'creativeSet',
          [...concepts, ...additions],
          `Uploaded ${additions.length} creative${additions.length === 1 ? '' : 's'} to the set`,
        );
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const metaCount = included.reduce(
    (sum, c) =>
      sum +
      (c.renditions ?? []).filter((r) => r.platform === 'meta' && r.enabled !== false).length,
    0,
  );
  const googleCount = included.reduce(
    (sum, c) =>
      sum +
      (c.renditions ?? []).filter(
        (r) => (r.platform === 'google' || r.platform === 'google_ads') && r.enabled !== false,
      ).length,
    0,
  );

  if (concepts.length === 0) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border-2 border-dashed border-border p-10 text-center text-sm text-ink-3">
          <p className="mb-1 text-ink-2">Creative concepts will appear here</p>
          Ask in chat to generate concepts, or upload your own creatives below.
        </div>
        {!readonly && (
          <UploadZone uploading={uploading} fileInputRef={fileInputRef} onFiles={handleUpload} />
        )}
      </div>
    );
  }

  const imageOf = (c: CreativeConcept) =>
    (c.variations?.find((v) => v.id === c.selectedVariation) ?? c.variations?.[0])?.assetUrl;
  const sel = concepts.find((c) => c.id === selectedId) ?? concepts.find((c) => c.status !== 'generating') ?? concepts[0];
  const selIdx = concepts.findIndex((c) => c.id === sel.id);
  const selIncluded = sel.included !== false;
  const selName = sel.concept ?? sel.id;
  const isCarousel = (sel.cards?.length ?? 0) >= 2;
  const cardSources = included.filter((c) => imageOf(c));
  const ghost = 'inline-flex min-h-[40px] items-center gap-2 rounded-btn px-3.5 font-display text-sm font-bold text-ink shadow-ring-line transition-shadow hover:shadow-ring-ink disabled:opacity-50';

  return (
    <div className="flex flex-col gap-[18px]">
      {/* Ideas */}
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="m-0 font-display text-[26px] font-extrabold tracking-[-0.02em] text-ink">
          {concepts.length} idea{concepts.length === 1 ? '' : 's'} · pick what runs
        </h2>
        {!readonly && (
          <div className="flex gap-2">
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleUpload(e.target.files)} />
            <button type="button" className={ghost} disabled={uploading} onClick={() => fileInputRef.current?.click()}>{uploading ? 'Uploading…' : 'Upload my own'}</button>
            <button type="button" className={ghost} onClick={() => setShowLibrary(true)}>From library</button>
          </div>
        )}
      </div>
      <p className="m-0 -mt-2 text-small text-ink-2">
        In the campaign: <b className="text-ink">{included.length} idea{included.length === 1 ? '' : 's'}</b> → Meta {metaCount} creative{metaCount === 1 ? '' : 's'} · Google {googleCount} display asset{googleCount === 1 ? '' : 's'}. The platforms rotate them; winners surface in Live.
      </p>

      <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(min(200px,100%),1fr))]">
        {concepts.map((concept, i) => {
          const on = concept.id === sel.id;
          const inSet = concept.included !== false;
          if (concept.status === 'generating') return (
            <button key={concept.id} type="button" onClick={() => setSelectedId(concept.id)} aria-pressed={on}
              className={cn('flex flex-col gap-2.5 rounded-card bg-card p-3 text-left shadow-[inset_0_0_0_2px_rgb(var(--c-work-dot))]')}>
              <span className="grid aspect-square place-items-center rounded-btn bg-working-stripes text-small font-bold text-work">Making…</span>
              <span className="flex flex-col gap-1 px-1">
                <span className="truncate font-display text-[15px] font-bold text-ink">{concept.concept ?? concept.id}</span>
                <span className="truncate text-xs font-semibold text-work">{concept.pendingDirection ? `From your note · ${concept.pendingDirection}` : 'New images · ~1 min'}</span>
              </span>
            </button>
          );
          const failed = concept.status === 'failed';
          return (
            <button key={concept.id} type="button" onClick={() => setSelectedId(concept.id)} aria-pressed={on}
              className={cn('flex flex-col gap-2.5 rounded-card bg-card p-3 text-left transition-shadow',
                on ? 'shadow-[inset_0_0_0_2.5px_rgb(var(--c-ink-1))]' : failed ? 'shadow-[inset_0_0_0_2px_rgb(var(--c-bad))]' : 'hover:shadow-ring-line',
                isFlashing(`creativeSet.${concept.id}`) && 'canvas-flash')}>
              <span className={cn('block aspect-square overflow-hidden rounded-btn bg-placeholder-stripes', (!inSet || failed) && 'opacity-50')}>
                {imageOf(concept) ? <img src={imageOf(concept)} alt="" className="h-full w-full object-cover" /> : <ConceptArt concept={concept} index={i} />}
              </span>
              <span className="flex flex-col gap-1 px-1">
                <span className="truncate font-display text-[15px] font-bold text-ink">{concept.concept ?? concept.id}</span>
                <span className={cn('truncate text-xs', failed ? 'font-semibold text-bad' : !inSet ? 'font-semibold text-ink-3' : 'text-ink-2')}>
                  {failed ? (concept.failureReason ?? 'Image generation failed')
                    : !inSet ? 'Left out'
                    : [concept.source === 'uploaded' ? 'Your ad' : null, (concept.cards?.length ?? 0) >= 2 ? 'Carousel' : null,
                       concept.copy?.meta?.headline ? `“${concept.copy.meta.headline}”` : null].filter(Boolean).join(' · ') || 'Ready'}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* The selected idea */}
      <div className={cn('flex flex-wrap gap-6 rounded-panel bg-card p-6', isFlashing(`creativeSet.${sel.id}`) && 'canvas-flash')}>
        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-3.5">
          <div className="text-label uppercase text-ink-3">{selName} · {sel.status === 'generating' ? 'making new images' : 'takes'}</div>
          {sel.status === 'failed' ? (
            <div className="flex flex-col items-start gap-3 rounded-btn bg-bad-soft p-4 text-small text-ink">
              {sel.failureReason ?? 'Image generation failed.'} Other ideas are unaffected.
              {!readonly && <button type="button" className={ghost} disabled={busy[sel.id]} onClick={() => regenerate(sel)}>{busy[sel.id] ? 'Retrying…' : 'Retry'}</button>}
            </div>
          ) : sel.status === 'generating' ? (
            <div className="rounded-btn bg-working-stripes p-4 text-small font-semibold text-work">Making 3 new takes{sel.pendingDirection ? ` — ${sel.pendingDirection}` : ''}… You can keep working on the other ideas.</div>
          ) : (
            <>
              {(sel.variations?.length ?? 0) > 0 && (
                <div className="flex flex-wrap items-end gap-2.5">
                  {sel.variations!.map((v) => (
                    <button key={v.id} type="button" disabled={readonly} title={`Take ${v.id}`}
                      onClick={() => onEdit(`creativeSet[${selIdx}].selectedVariation`, v.id, `Picked take ${v.id} for "${selName}"`)}
                      className={cn('h-[88px] w-[88px] overflow-hidden rounded-btn bg-placeholder-stripes',
                        sel.selectedVariation === v.id ? 'shadow-[0_0_0_3px_rgb(var(--c-ink-1))]' : 'opacity-80 hover:opacity-100')}>
                      {v.assetUrl ? <img src={v.assetUrl} alt={`Take ${v.id}`} className="h-full w-full object-cover" /> : null}
                    </button>
                  ))}
                  {(sel.versions?.length ?? 0) > 0 && !readonly && (
                    <button type="button" disabled={busy[sel.id]} onClick={() => undoRegen(sel)} className="pb-1 text-small font-semibold text-brand-ink hover:underline">↩ Use the earlier takes</button>
                  )}
                </div>
              )}
              {selIncluded && (sel.renditions?.length ?? 0) > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-semibold text-ink-3">Sizes it runs in</div>
                  <div className="flex flex-wrap gap-1.5">
                    {sel.renditions!.map((rendition, j) => {
                      if (!inStrategy(rendition.platform)) return null;
                      const on = rendition.enabled !== false;
                      return (
                        <button key={rendition.id} type="button" disabled={readonly} aria-pressed={on}
                          onClick={() => onEdit(`creativeSet[${selIdx}].renditions[${j}].enabled`, !on,
                            `${!on ? 'Enabled' : 'Disabled'} ${formatLabel(rendition.format, rendition.platform)} for "${selName}"`)}
                          className={cn('inline-flex h-8 items-center rounded-full px-3 text-xs font-semibold transition-colors',
                            on ? 'bg-navy text-on-navy' : 'bg-inset text-ink-3 line-through')}>
                          {formatLabel(rendition.format, rendition.platform)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {!readonly && (
                <div role="group" aria-label="How the ad carries its text" className="inline-flex w-max gap-1 rounded-btn bg-inset p-1">
                  {([true, false] as const).map((baked) => (
                    <button key={String(baked)} type="button" aria-pressed={(sel.bakedCopy === true) === baked}
                      title={baked ? 'The image already contains its headline and button — published as is.' : 'Plain image — Meta assembles and tests your headlines.'}
                      onClick={() => (sel.bakedCopy === true) !== baked && onEdit(`creativeSet[${selIdx}].bakedCopy`, baked,
                        baked ? `"${selName}" → finished ad (baked-in text)` : `"${selName}" → plain image (Meta assembles copy)`)}
                      className={cn('min-h-[34px] rounded-[9px] px-3 text-xs font-semibold', (sel.bakedCopy === true) === baked ? 'bg-navy text-on-navy' : 'text-ink')}>
                      {baked ? 'Finished ad · text in image' : 'Plain image · Meta adds text'}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          {!readonly && (
            <div className="flex flex-wrap gap-2">
              {sel.source !== 'uploaded' && sel.status !== 'generating' && (
                <button type="button" className={ghost} onClick={() => toggleSet(setRegenOpen, sel.id)} aria-expanded={regenOpen.has(sel.id)}>Remake with a note</button>
              )}
              {(isCarousel || cardSources.length >= 2) && (
                <button type="button" className={ghost}
                  title="Combine the included ideas' images into one carousel ad (reuses existing images — no new generation)"
                  onClick={() => {
                    if (isCarousel) onEdit(`creativeSet[${selIdx}].cards`, null, `Removed carousel from "${selName}"`);
                    else {
                      const cards = cardSources.map((c) => ({ id: `card_${c.id}`, assetUrl: imageOf(c), headline: c.copy?.meta?.headline, source: c.source ?? 'generated', sourceConceptId: c.id }));
                      onEdit(`creativeSet[${selIdx}].cards`, cards, `Combined ${cards.length} images into a carousel`);
                    }
                  }}>{isCarousel ? 'Remove carousel' : 'Make a carousel'} <span className="font-money text-xs text-ok">free</span></button>
              )}
              <button type="button" className={ghost}
                onClick={() => onEdit(`creativeSet[${selIdx}].included`, !selIncluded, `${!selIncluded ? 'Included' : 'Excluded'} concept "${selName}"`)}>
                {selIncluded ? 'Leave out' : 'Put back in'}
              </button>
            </div>
          )}
          {regenOpen.has(sel.id) && !readonly && (
            <div className="flex flex-col gap-2.5 rounded-card bg-ground p-3.5">
              <label htmlFor={`regen-${sel.id}`} className="text-small font-semibold text-ink-2">What should change? (optional)</label>
              <input id={`regen-${sel.id}`} type="text" value={regenText[sel.id] ?? ''} maxLength={500}
                onChange={(e) => setRegenText((prev) => ({ ...prev, [sel.id]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); regenerate(sel, regenText[sel.id]?.trim() || undefined); } }}
                placeholder="e.g. warmer light, outdoors"
                className="min-h-[44px] rounded-btn border-0 bg-card px-3.5 text-[15px] text-ink shadow-ring-line placeholder:text-ink-3 focus:outline-none focus:shadow-ring-ink" />
              <div className="flex flex-wrap items-center gap-1.5">
                {QUICK_CHIPS.map(([label, text]) => (
                  <button key={label} type="button" onClick={() => setRegenText((prev) => ({ ...prev, [sel.id]: text }))}
                    className="min-h-[34px] rounded-full bg-inset px-3 text-[13px] font-semibold text-navy-ink hover:bg-line">{label}</button>
                ))}
                <button type="button" disabled={busy[sel.id]} onClick={() => regenerate(sel, regenText[sel.id]?.trim() || undefined)}
                  className="ml-auto inline-flex min-h-[40px] items-center rounded-btn bg-navy px-4 font-display text-sm font-bold text-on-navy hover:bg-navy-3 disabled:opacity-50">
                  {busy[sel.id] ? 'Starting…' : 'Make new takes'}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-3">
          <div className="text-label uppercase text-ink-3">Preview</div>
          {sel.status !== 'generating' && sel.status !== 'failed'
            ? <CreativePreviews state={state} concept={sel} index={selIdx} uploadedImages={uploadedImages} onAdjustmentSave={handleAdjustmentSave} />
            : <div className="aspect-square max-w-[300px] rounded-card bg-placeholder-stripes" />}
          <div className="text-xs text-ink-3">Finished ads show whole. Plain photos can be dragged to reposition.</div>
        </div>
      </div>

      {/* Library picker — reuses MediaLibrary in selection mode; a picked finished ad
          becomes an uploaded-source concept via handleLibrarySelect. */}
      {showLibrary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/55 p-4" onClick={() => setShowLibrary(false)}>
          <div className="max-h-[85vh] w-full max-w-4xl overflow-auto rounded-[24px] bg-card p-7 shadow-decide" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between gap-3">
              <h4 className="m-0 font-display text-[26px] font-extrabold tracking-[-0.02em] text-ink">Choose a finished ad</h4>
              <button type="button" onClick={() => setShowLibrary(false)} className="grid h-10 w-10 place-items-center rounded-btn bg-ground text-ink" aria-label="Close">✕</button>
            </div>
            <MediaLibrary selectionMode mediaType="all" defaultGroup="uploads" onSelect={handleLibrarySelect} />
          </div>
        </div>
      )}

      {!readonly && included.length > 0 && (
        <p className="m-0 text-xs leading-snug text-ink-3">
          Each idea in the campaign ships as an AI-optimised ad — cropped per placement at the position you set, headlines tested automatically,
          spend shifted to winners. Finished ads and carousels run as they are. 4–6 distinct ideas give the platform the most to test.
        </p>
      )}

      {/* Decide bar */}
      {!readonly && (
        <section aria-label="Decide" className="sticky bottom-4 z-10 mt-2 flex flex-wrap items-center justify-between gap-3.5 rounded-panel bg-navy py-3.5 pl-[22px] pr-3.5 text-on-navy shadow-decide">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="font-display text-base font-bold">{included.length} of {concepts.length} idea{concepts.length === 1 ? '' : 's'} in the campaign</span>
            <span className="text-small text-on-navy-2">
              {anyGenerating ? 'Images are still being made — you can keep choosing meanwhile.'
                : included.length < 1 ? 'Put at least one idea in the campaign to continue.'
                : 'Nothing is published or spent yet. Next: pick accounts and timing.'}
            </span>
          </div>
          <button type="button" onClick={() => onApprove('creative')} disabled={approving || included.length < 1}
            className="inline-flex min-h-[52px] items-center rounded-btn bg-brand px-[22px] font-display text-base font-bold text-brand-on hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-60">
            {approving ? 'Approving…' : 'Approve creative →'}
          </button>
        </section>
      )}
    </div>
  );
};

const UploadZone: React.FC<{
  uploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFiles: (files: FileList | null) => void;
}> = ({ uploading, fileInputRef, onFiles }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={() => fileInputRef.current?.click()}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
    }}
    onDragOver={(e) => e.preventDefault()}
    onDrop={(e) => {
      e.preventDefault();
      onFiles(e.dataTransfer.files);
    }}
    className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border p-3.5 text-xs text-ink-3 hover:border-primary hover:text-primary transition-colors"
  >
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      multiple
      className="hidden"
      onChange={(e) => onFiles(e.target.files)}
    />
    {uploading ? 'Uploading…' : '⬆ Upload your own creatives to the set'}
  </div>
);
